import type {
  FunctionEnabled,
  GeolocationSupport,
  Resource,
  ResourceType,
} from './common';
import { AuthenticationMethod } from './user';

export enum NotificationChannelType {
  Email = 1,
  Sms = 2,
}

export interface NewCommunityConfig {
  authenticationMethods: {
    enabled: Boolean;
    type: AuthenticationMethod;
    default?: boolean;
  }[];
  functions: {
    enabled: boolean;
    type: number;
  };
  lifetime: number;
  maxDiskSpace: number;
  maxExternalUsersNumber: number;
  maxExternalUsersNumberSigning: number;
  maxUsersNumber: number;
  onlyCH: boolean;
  recaptchaSiteKey?: string;
}

export interface ContractTemplate {
  id: number;
  name: string;
  type: number;
  profile: number;
  flatRate: number;
  language: string;
  active: boolean;
  date: number;
  contract: boolean;
}

export interface AbstractCommunityConfigAdmin {
  adminFullName: string;
  adminGender: number;
  adminCompany: string;
  adminDepartment: string;
  adminFirstName: string;
  adminLastName: string;
  adminMobile: string;
  adminEmail: string;
  adminAddress: string;
  adminZip: string;
  adminLocality: string;
  adminCountry: string;
  adminRegion: string;
}

export interface NotificationChannel {
  type: NotificationChannelType;
  enabled: boolean;
}

export interface SendLoginChannel {
  type: NotificationChannelType;
  enabled: boolean;
}

export interface PasswordPolicy {
  type: number;
  enabled: boolean;
}

interface CommunityAuthenticationMethod {
  type: AuthenticationMethod;
  enabled: boolean;
  mobileMandatory: boolean;
}

export interface AbstractCommunityAdminProperties extends AbstractCommunityConfigAdmin {
  notificationChannels: NotificationChannel[];
  sendLoginInfoChannels: SendLoginChannel[];
  functions: FunctionEnabled[];
  emailMandatory: boolean;
  mobilePhoneMandatory: boolean;
  signatureSeal: boolean;
  signatureSealKeyLabel: string;
  authenticationMethods: CommunityAuthenticationMethod[];
  loginName: string;
  status: number;
  existingExternalUsers: number;
  existingOneTimeGuests: number;
  maxExternalUsers: number;
  oneTimeGuestsAllowed: boolean;
  maxUsers: number;
  existingUsers: number;
  availableUsers: number;
  availableExternalUsers: number;
  maxDiskSpace: number;
  maxDiskSpaceInBytes: number;
  usedDiskSpaceInBytes: number;
  freeDiskSpaceInBytes: number;
  basicSignature: boolean;
  advancedSignatureZertes: boolean;
  advancedSignatureFastTrack: boolean;
  advancedSignatureEidas: boolean;
  qualifiedSignatureZertes: boolean;
  qualifiedSignatureEidas: boolean;
  signature: boolean;
  name: string;
  notes: string;
  antivirus: boolean;
  webdav: boolean;
  activeDirectory: boolean;
  communityUrl: string;
  customMessagesPrefix: string;
  coupon: string;
  profileId: number;
  testSignatures: number;
  customerId: number;
  proNumber: string;
  reporting: boolean;
  conditionsOfUse: number;
  privasphere: boolean;
  signatureCustom: boolean;
  signatureCustomClaimedIdentity: string;
  communityManagers: number[];
  emailFilter: string;
  flatRate: boolean;
  guestsUnlimitedAccess: boolean;
  swisscomSign: boolean;
  systemToken: boolean;
}

export interface CommunityAdminProperties extends AbstractCommunityAdminProperties {}

export interface Community extends Resource<ResourceType.COMMUNITY> {
  loginName: string;
  defaultLanguage: string;
  defaultAuthenticationMethod: number;
  passwordMinLength: number;
  passwordMinQuality: number;
  passwordPolicies: PasswordPolicy[];
  communityGuestCreation: number;
  passwordChangeType: number;
  passwordPreventReuse: number;
  hasCustomLogo: boolean;
  isSpaceTresholdPriorityHigh: boolean;
  passwordMaxLoginTries: number;
  generateOwnGridCard: boolean;
  spaceTreshold: number;
  spaceTresholdPriorityHigh: boolean;
  hasSpaceAlert: boolean;
  hasReminderAlert: boolean;
  communityAdminProperties: CommunityAdminProperties;
  communityNotificationChannels: NotificationChannel[];
  communityOneTimeGuestsAllowed: boolean;
  cec: string;
  cecDefined: boolean;
  maskMobile: boolean;
  conversationsDiskSpace: number;
  maps: boolean;
  disableUsersDays: number;
  geolocationSupport: GeolocationSupport;
  timeout: number;
  homonyms: boolean;
  sessionLifetime: number;
  pagingSize: number;
  loginNameChanged: boolean;
  editorDeletePermission: boolean;
  signatureSealPrivateKey: string;
  signatureSealCertificate: string;
  signatureSealFinalDate: number;
  signatureSeal: boolean;
  signatureCustomPrivateKey: string;
  signatureCustomCertificate: string;
  signatureCustomFinalDate: number;
  signatureCustom: boolean;
  signingRoomMaxLifetime: number;
  guestsDeleteAfterDays: number;
  notificationsCustom: boolean;
  notificationsCustomFooter: string;
  signingRoomGuestsVisibility: boolean;
  watermarkPrint: boolean;
  watermarkFormat: string;
  url: string;
  supportEmail: string;
  supportPhone: string;
  customCommunityLogo?: string | boolean;
  conditionsOfUseDate?: number;
}

export interface ContractTemplate {
  id: number;
  name: string;
  type: number;
  profile: number;
  flatRate: number;
  language: string;
  active: boolean;
  date: number;
  contract: boolean;
}

export interface NotificationSettingsInfo {
  delivery: number;
  days: number;
  timeAsInt: number;
  channels: NotificationChannel[];
  time: number;
}

export interface NotificationSettings {
  rid: number;
  normalPriority: NotificationSettingsInfo;
  highPriority: NotificationSettingsInfo;
  editable: boolean;
  defaultSettings: boolean;
  conversation: NotificationSettingsInfo;
  support: NotificationSettingsInfo;
}

export interface InputStream {}

export interface CommunityTemplate {
  templateId: number;
  type: number;
  subtype: number;
  name: string;
  content: InputStream;
  contentType: string;
  size: number;
  permanent: boolean;
}

export interface LdapSettings {
  cid: number;
  serverConnection: string;
  domainName: string;
  filters: string;
  adUsername: string;
  adPassword: string;
  passwordType: number;
  groupVisible: boolean;
  updateDate: number;
}

export interface CommunityAzureAd {
  sso: boolean;
  sync: boolean;
  tenantId: string;
  clientId: string;
  secretKey: string;
  signinUrl: string;
  signinRedirectUrl: string;
  graphUrl: string;
  provisioningToken: string;
  provisioningTokenExpiration: number;
}

export interface ConditionsOfUseText {
  empty: boolean;
  version: number;
  language: string;
  title: string;
  text: string;
  defaultLanguage: boolean;
  fileName: string;
  fileSize: number;
}

export interface ConditionsOfUse {
  version: number;
  enabled: boolean;
  newVersion: boolean;
  texts: ConditionsOfUseText[];
}

export type ConditionsOfUseUpdate = Omit<ConditionsOfUse, 'texts'> & {
  texts?: Pick<ConditionsOfUseText, 'defaultLanguage' | 'text' | 'language'>[];
};
